/**
 * Phase 3 Fix (Feb 2026) — Budget slider bound to Master-Cap.
 *
 * Fixes the Phase-3 re-verification bug: slider now sources both the
 * cap and the today's-committed/remaining amounts from the SAME
 * backend endpoint, and refuses to accept values above the master
 * daily cap.
 *
 *   Endpoint: GET /api/v1/budgets/status
 *     → { budget: { daily_budget_amount_cents, ... } | null,
 *         totals_cents: {
 *             budget_daily, spent_real, reserved_real,
 *             unreconciled_real, available_real, spent_synthetic
 *         } }
 *
 * Rules (Auftrag §7.5 / Korrektur 10):
 *   * Slider-Max = master daily_budget_amount_cents. Values above
 *     the cap CAN NOT be selected — the slider is hard-clamped.
 *   * daily_cap        = master daily_budget_amount_cents
 *     daily_remaining  = totals_cents.available_real
 *     already_committed= spent_real + reserved_real
 *     (all three from the SAME response — no mixed sources)
 *   * If daily_cap is null (no active master budget): slider is
 *     DISABLED with the plain-german message "Erst Master-Budget in
 *     Einstellungen anlegen" — no fallback default value.
 *   * If the value exceeds the today's remaining (but still <= cap):
 *     an amber banner appears with plain-german text
 *     "Erhöhung des Unternehmens-Rahmens erfordert menschliche
 *      Freigabe (Klasse C)" and a deep-link to
 *     /app/settings#budget.
 *   * Server-side enforcement (Phase-1 PolicyGuard + BudgetService)
 *     stays the actual chokepoint. The UI clamp is additive, not a
 *     replacement.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { deriveBudget, explainBudget } from "../../lib/budget-recommendation";
import { Slider } from "../ui/slider";
import { Badge } from "../ui/badge";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";
import { AlertTriangle, Info, Lock } from "lucide-react";

function fmtEur(cents) {
  if (cents == null || Number.isNaN(cents)) return "—";
  return `${Math.round(cents / 100)} €`;
}

export default function BudgetSlider({
  value_cents,
  onChange,
  target_leads_per_month = 30,
  category = null,
  duration_days = 30,
  testid = "budget-slider",
}) {
  const [snap, setSnap] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/budgets/status")
      .then((r) => { if (!cancelled) { setSnap(r.data || null); setLoaded(true); } })
      .catch(() => { if (!cancelled) { setSnap(null); setLoaded(true); } });
    return () => { cancelled = true; };
  }, []);

  const budget = snap?.budget || null;
  const totals = snap?.totals_cents || {};
  // "Effectively active" — the concept requires an active, in-window
  // budget for any paid execution. Paused/ended/not-yet-started rows
  // count as no-master for the UI, matching the server-side behaviour
  // of BudgetService.reserve which refuses on the same conditions.
  const now = Date.now();
  const inWindow = (b) => {
    if (!b) return false;
    if (b.starts_at && new Date(b.starts_at).getTime() > now) return false;
    if (b.ends_at && new Date(b.ends_at).getTime() < now) return false;
    return true;
  };
  const master_state = !budget
    ? "no-master-budget"
    : budget.status !== "active"
      ? (budget.status === "paused" ? "master-paused"
         : budget.status === "ended"  ? "master-ended"
         : "master-budget-inactive")
    : !inWindow(budget)
      ? (budget.starts_at && new Date(budget.starts_at).getTime() > now
          ? "master-not-started" : "master-ended")
    : "ready";
  const has_master = master_state === "ready";
  const daily_cap = has_master ? budget.daily_budget_amount_cents : null;
  const already_committed =
    has_master
      ? Math.max(0, (totals.spent_real || 0) + (totals.reserved_real || 0))
      : null;
  const daily_remaining =
    has_master
      ? Math.max(0, totals.available_real ?? Math.max(0, daily_cap - already_committed))
      : null;

  // Hard-clamp: slider-max = master cap. Values above the cap are
  // unreachable via the slider input.
  const max_cents = has_master ? daily_cap : 0;
  const clamped = has_master
    ? Math.min(Math.max(value_cents ?? 0, 0), max_cents)
    : 0;
  const exceedsRemaining =
    has_master && daily_remaining != null && clamped > daily_remaining;

  const recommendation = useMemo(
    () => deriveBudget({ target_leads_per_month, category, duration_days }),
    [target_leads_per_month, category, duration_days],
  );

  // Inactive / missing master branch — slider is not rendered.
  if (loaded && !has_master) {
    const label = {
      "no-master-budget":       "Erst Master-Budget in Einstellungen anlegen",
      "master-paused":          "Master-Budget ist pausiert — in Einstellungen wieder aktivieren",
      "master-ended":           "Master-Budget ist abgelaufen — neues Budget in Einstellungen anlegen",
      "master-not-started":     "Master-Budget startet später — Startdatum in Einstellungen prüfen",
      "master-budget-inactive": "Master-Budget ist nicht aktiv — in Einstellungen prüfen",
    }[master_state];
    return (
      <div
        className="rounded-md border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm space-y-2"
        data-testid={testid}
        data-state={master_state}
      >
        <div className="flex items-start gap-2">
          <Lock className="w-4 h-4 text-amber-600 mt-0.5" />
          <div className="space-y-1">
            <div className="font-medium" data-testid={`${testid}-no-master`}>
              {label}
            </div>
            <div className="text-xs text-muted-foreground">
              Ohne wirksames Master-Budget legt VIONA keine neuen Kosten an —
              der Kampagnen-Slider bleibt erst inaktiv, bis der Rahmen wieder
              gültig aktiv ist.
            </div>
          </div>
        </div>
        <Link
          to="/app/settings"
          className="inline-block text-xs underline text-amber-700 dark:text-amber-400"
          data-testid={`${testid}-open-settings`}
        >
          Zur Budget-Verwaltung →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={testid} data-state={loaded ? "ready" : "loading"}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <Metric
          label="Wiederkehrende Tages-Cap"
          value={fmtEur(daily_cap)}
          testid={`${testid}-cap`}
          help="Höchstbetrag, den VIONA pro Tag insgesamt für Werbung bindet. Ändert sich nur über eine geschützte Freigabe."
        />
        <Metric
          label="Restbetrag heute"
          value={fmtEur(daily_remaining)}
          testid={`${testid}-remaining`}
          help="Was von der heutigen Cap noch übrig ist, nach Abzug bereits gebundener Kampagnen."
        />
        <Metric
          label="Bereits gebunden"
          value={fmtEur(already_committed)}
          testid={`${testid}-committed`}
          help="Summe aus aktiven Kampagnen-Tagesbudgets (Spent + Reserved)."
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span>Tagesbudget dieser Kampagne</span>
          <span className="font-mono" data-testid={`${testid}-value`}>
            {fmtEur(clamped)}
          </span>
        </div>
        <Slider
          data-testid={`${testid}-input`}
          aria-valuemax={Math.round(max_cents / 100)}
          value={[Math.round(clamped / 100)]}
          onValueChange={(v) =>
            onChange(Math.min(Math.round(v[0]) * 100, max_cents))
          }
          min={0}
          max={Math.round(max_cents / 100)}
          step={1}
        />
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>0 €</span>
          <span className="ml-auto">
            {fmtEur(max_cents)} (Master-Cap)
          </span>
        </div>
      </div>

      <TooltipProvider>
        <div
          className="rounded-md border bg-muted/40 p-3 text-xs space-y-1"
          data-testid={`${testid}-recommendation`}
        >
          <div className="flex items-center gap-2">
            <Badge variant="outline">Empfehlung</Badge>
            <span
              className="font-mono"
              data-testid={`${testid}-recommendation-value`}
            >
              {recommendation.daily_eur} €/Tag
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`${testid}-recommendation-info`}
                >
                  <Info className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                className="max-w-sm whitespace-pre-line text-xs"
              >
                {explainBudget(recommendation)}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="text-muted-foreground">{recommendation.formula}</div>
        </div>
      </TooltipProvider>

      {exceedsRemaining && (
        <div
          className="rounded-md border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/20 p-2 text-xs flex items-start gap-2"
          data-testid={`${testid}-warning-exceeds-remaining`}
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
          <div className="space-y-1">
            <div>
              Der Betrag überschreitet den heutigen Restbetrag ({fmtEur(daily_remaining)}).
              Erhöhung des Unternehmens-Rahmens erfordert menschliche Freigabe
              (Klasse&nbsp;C).
            </div>
            <Link
              to="/app/settings"
              className="underline text-amber-700 dark:text-amber-400"
              data-testid={`${testid}-open-settings`}
            >
              Zur Budget-Verwaltung →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, testid, help }) {
  return (
    <div
      className="rounded-md border bg-background/70 px-2 py-1.5"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">{label}</span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground">
                <Info className="w-3 h-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              {help}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="font-mono text-sm mt-0.5">{value}</div>
    </div>
  );
}
